import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  Fade,
  alpha,
  useMediaQuery,
  Link,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SendIcon from "@mui/icons-material/Send";

export const HeroSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [formData, setFormData] = React.useState({
    nome: "",
    telefone: "",
    email: "",
    tipoPlano: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("Dados do formulário:", formData);
    // Lógica para enviar os dados do formulário
  };

  // Array de checkmarks para destaque
  const highlights = [
    "Atendimento disponível 24/7",
    "Até 50% de economia no seu plano",
    "Mais de 10.000 clientes satisfeitos",
  ];

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: { xs: 6, sm: 10, md: 12 },
        pb: { xs: 8, sm: 12, md: 14 },
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
      }}
    >
      {/* Elementos decorativos */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.light,
            0.2
          )} 0%, ${alpha(theme.palette.primary.main, 0.3)} 100%)`,
          filter: "blur(60px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -150,
          left: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.secondary.light,
            0.2
          )} 0%, ${alpha(theme.palette.secondary.main, 0.3)} 100%)`,
          filter: "blur(80px)",
        }}
      />

      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
          {/* Coluna da esquerda - textos */}
          <Grid item xs={12} md={6}>
            <Fade in={true} timeout={1000}>
              <Box sx={{ position: "relative", zIndex: 2 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: 30,
                    px: 2,
                    py: 0.75,
                    mb: 3,
                  }}
                >
                  <HealthAndSafetyIcon
                    sx={{
                      color: theme.palette.primary.main,
                      mr: 1,
                      fontSize: 18,
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                  >
                    Planos de Saúde Personalizados
                  </Typography>
                </Box>

                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    mb: 3,
                    fontWeight: 800,
                    fontSize: { xs: "2.5rem", sm: "3.25rem", md: "4rem" },
                    lineHeight: 1.2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 70%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Seu plano de saúde com economia real
                </Typography>

                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    mb: 4,
                    fontWeight: 400,
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                    fontSize: { xs: "1rem", md: "1.125rem" },
                  }}
                >
                  Encontre o plano de saúde ideal para você, sua família ou sua
                  empresa com as melhores condições do mercado. Economize até
                  50% na mensalidade mantendo a qualidade do atendimento.
                </Typography>

                {/* Destaques */}
                <Box sx={{ mb: 4 }}>
                  {highlights.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: alpha(
                            theme.palette.secondary.main,
                            0.1
                          ),
                          mr: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: theme.palette.secondary.main,
                          }}
                        />
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Botão Mobile (aparece apenas em telas pequenas) */}
                {isMobile && (
                  <Button
                    variant="contained"
                    size="large"
                    color="primary"
                    href="#formulario"
                    fullWidth
                    sx={{
                      py: 2,
                      mb: 4,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    }}
                  >
                    Solicitar Cotação Gratuita
                  </Button>
                )}

                {/* Estatísticas */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                  }}
                >
                  <Box>
                    <Typography
                      variant="h3"
                      component="p"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                      }}
                    >
                      10k+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Clientes satisfeitos
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="h3"
                      component="p"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                      }}
                    >
                      50%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Economia média
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="h3"
                      component="p"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                      }}
                    >
                      100%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Aprovação ANS
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Grid>

          {/* Coluna da direita - formulário */}
          <Grid item xs={12} md={6} id="formulario">
            <Fade in={true} timeout={1200}>
              <Paper
                elevation={6}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  background: theme.palette.background.paper,
                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                  position: "relative",
                  zIndex: 2,
                  overflow: "hidden",
                }}
              >
                {/* Elementos decorativos do formulário */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -50,
                    right: -50,
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    background: alpha(theme.palette.primary.light, 0.1),
                    zIndex: -1,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -30,
                    left: -30,
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: alpha(theme.palette.secondary.light, 0.1),
                    zIndex: -1,
                  }}
                />

                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <MedicalServicesIcon
                    sx={{
                      color: theme.palette.primary.main,
                      mr: 1.5,
                      fontSize: 24,
                    }}
                  />
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                  >
                    Solicite sua cotação gratuita
                  </Typography>
                </Box>

                <Typography
                  variant="body1"
                  sx={{ mb: 4, color: theme.palette.text.secondary }}
                >
                  Preencha seus dados e receba uma análise personalizada das
                  melhores opções para você.
                </Typography>

                <Box component="form" noValidate onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Nome completo"
                    variant="outlined"
                    margin="normal"
                    required
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Telefone / WhatsApp"
                    variant="outlined"
                    margin="normal"
                    required
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleChange}
                    type="tel"
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="E-mail"
                    variant="outlined"
                    margin="normal"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    type="email"
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormControl fullWidth margin="normal" sx={{ mb: 4 }}>
                    <InputLabel id="tipo-plano-label">Tipo de plano</InputLabel>
                    <Select
                      labelId="tipo-plano-label"
                      id="tipo-plano"
                      name="tipoPlano"
                      value={formData.tipoPlano}
                      label="Tipo de plano"
                      onChange={(e) =>
                        handleChange(e as React.ChangeEvent<HTMLInputElement>)
                      }
                    >
                      <MenuItem value="individual">Individual</MenuItem>
                      <MenuItem value="familiar">Familiar</MenuItem>
                      <MenuItem value="empresarial">Empresarial</MenuItem>
                      <MenuItem value="odontologico">Odontológico</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    type="submit"
                    endIcon={<SendIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 8,
                      fontWeight: 600,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: `0 8px 20px ${alpha(
                          theme.palette.primary.main,
                          0.4
                        )}`,
                      },
                    }}
                  >
                    Solicitar Cotação Gratuita
                  </Button>

                  <Typography
                    variant="caption"
                    display="block"
                    align="center"
                    sx={{ mt: 2, color: theme.palette.text.secondary }}
                  >
                    Seus dados estão protegidos com nossa{" "}
                    <Link
                      href="#"
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: "none",
                        fontWeight: 500,
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      política de privacidade
                    </Link>
                  </Typography>
                </Box>
              </Paper>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
